package com.appsyde.shared.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.appsyde.shared.app.navigation.AppPaths
import com.appsyde.shared.app.presentation.SessionViewModel
import com.appsyde.shared.app.presentation.ticket.TicketViewModel
import com.appsyde.shared.app.presentation.tickets.TicketsListViewModel
import com.appsyde.shared.clearSessionForLogout
import com.appsyde.shared.getAccessToken
import com.appsyde.shared.setAccessToken
import com.appsyde.shared.modules.account.domain.model.AccountRole
import com.appsyde.shared.modules.account.domain.model.GmailConnectionStatus
import com.appsyde.shared.modules.account.domain.model.OnboardingStatus
import com.appsyde.shared.modules.account.domain.usecase.GetMeUseCase
import com.appsyde.shared.modules.auth.domain.model.DomainResult
import com.appsyde.shared.modules.ticket.domain.repository.EmailRepository
import com.appsyde.shared.network.exception.InsufficientScopesException
import com.appsyde.shared.modules.auth.domain.repo.AuthRepository
import com.appsyde.shared.network.exception.NotAuthenticatedException
import com.appsyde.shared.presentation.screen.screen.BillingGateScreen
import com.appsyde.shared.presentation.screen.screen.BillingSuccessScreen
import com.appsyde.shared.presentation.screen.screen.LoadingScreen
import com.appsyde.shared.presentation.screen.screen.MainScreen
import com.appsyde.shared.presentation.screen.screen.OnboardingWizardScreen
import com.appsyde.shared.presentation.screen.screen.inbox.InboxPermissionsRequiredView
import com.appsyde.shared.presentation.style.style.AppStylesAccount
import com.appsyde.shared.presentation.style.style.AppStylesGlobal
import com.appsyde.shared.presentation.style.style.AppStylesHeader
import com.appsyde.shared.presentation.style.style.AppStylesLayout
import com.appsyde.shared.presentation.style.style.AppStylesCards
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.Style
import org.koin.core.context.GlobalContext

/**
 * Authenticated flow: load me, then either show a gate (reconnect Gmail, billing, onboarding)
 * or the main app content (tickets + MainScreen).
 */
@Composable
fun LoggedInShell(
    sessionViewModel: SessionViewModel,
    origin: String
) {
    var meInfo by remember { mutableStateOf<com.appsyde.shared.modules.account.domain.model.Me?>(null) }
    var meRefreshTrigger by remember { mutableStateOf(0) }
    var showBillingSuccess by remember { mutableStateOf(false) }
    var showPermissionsBeforeOnboarding by remember { mutableStateOf(false) }
    val getMeUseCase = remember { GlobalContext.get().get<GetMeUseCase>() }
    val emailRepository = remember { GlobalContext.get().get<EmailRepository>() }
    val authRepo = remember { GlobalContext.get().get<AuthRepository>() }

    LaunchedEffect(showBillingSuccess) {
        if (!showBillingSuccess) return@LaunchedEffect
        delay(5000)
        window.history.replaceState(null, "", "/")
        showBillingSuccess = false
    }

    LaunchedEffect(meRefreshTrigger) {
        if (getAccessToken().isNullOrBlank()) {
            runCatching {
                when (val res = authRepo.getSessionToken()) {
                    is DomainResult.Success ->
                        res.value?.takeIf { it.isNotBlank() }?.let { setAccessToken(it) }
                    is DomainResult.Failure -> { /* no token */ }
                }
            }
        }
        runCatching { getMeUseCase() }
            .onSuccess { result ->
                meInfo = result
                if (window.location.pathname.contains(AppPaths.BILLING_SUCCESS) && result?.mustSubscribe == false) {
                    window.history.replaceState(null, "", "/")
                    showBillingSuccess = true
                }
            }
            .onFailure { e ->
                if (e is NotAuthenticatedException) {
                    clearSessionForLogout()
                    sessionViewModel.refreshSession()
                } else {
                    meInfo = null
                }
            }
    }
    LaunchedEffect(Unit) {
        if (window.location.pathname.contains(AppPaths.BILLING_SUCCESS)) meRefreshTrigger++
    }

    // Avant d'afficher l'onboarding (étape "Invitez votre équipe"), vérifier que les scopes Gmail sont OK.
    LaunchedEffect(meInfo, meRefreshTrigger) {
        if (meInfo == null || meInfo?.onboardingStatus == OnboardingStatus.READY) return@LaunchedEffect
        runCatching { emailRepository.getInboxStats() }
            .onSuccess { showPermissionsBeforeOnboarding = false }
            .onFailure { e ->
                if (e is InsufficientScopesException || e.cause is InsufficientScopesException) {
                    showPermissionsBeforeOnboarding = true
                } else {
                    showPermissionsBeforeOnboarding = false
                }
            }
    }

    when {
        meInfo?.gmailConnectionStatus == GmailConnectionStatus.NEED_RECONNECT -> {
            val isOwner = meInfo?.role == AccountRole.OWNER
            InboxPermissionsRequiredView(
                onGrantPermissions = { sessionViewModel.startReconnectGmail(origin) },
                onLogout = { clearSessionForLogout(); sessionViewModel.logout() },
                title = "Reconnexion Gmail requise",
                bodyText = if (isOwner)
                    "La liaison avec votre boîte Gmail a expiré. Cliquez ci-dessous pour reconnecter votre compte."
                else
                    "La liaison Gmail du compte a expiré. Demandez au propriétaire du compte de reconnecter la boîte depuis son espace.",
                bodyBullets = null,
                primaryButtonLabel = "Reconnecter la boîte",
                showPrimaryButton = isOwner
            )
        }
        meInfo?.mustSubscribe == true -> {
            Style(AppStylesGlobal)
            Style(AppStylesAccount)
            Style(AppStylesHeader)
            Style(AppStylesLayout)
            Style(AppStylesCards)
            BillingGateScreen(
                meInfo = meInfo!!,
                onLogout = { clearSessionForLogout(); sessionViewModel.logout() }
            )
        }
        meInfo != null && meInfo?.onboardingStatus != OnboardingStatus.READY -> {
            if (showPermissionsBeforeOnboarding) {
                InboxPermissionsRequiredView(
                    onGrantPermissions = { sessionViewModel.startReconnectGmail(origin); meRefreshTrigger++ },
                    onLogout = { clearSessionForLogout(); sessionViewModel.logout() },
                    title = "Autorisez l'accès à Gmail",
                    showPrimaryButton = true
                )
            } else {
                OnboardingWizardScreen(onComplete = { meRefreshTrigger++ })
            }
        }
        meInfo == null -> {
            LoadingScreen()
        }
        else -> {
            LoggedInMainContent(
                sessionViewModel = sessionViewModel,
                origin = origin,
                showBillingSuccess = showBillingSuccess,
                onBillingSuccessDismiss = { showBillingSuccess = false }
            )
        }
    }
}

@Composable
private fun LoggedInMainContent(
    sessionViewModel: SessionViewModel,
    origin: String,
    showBillingSuccess: Boolean,
    onBillingSuccessDismiss: () -> Unit
) {
    val ticketViewModel = remember {
        try {
            GlobalContext.get().get<TicketViewModel>()
        } catch (t: Throwable) {
            t.printStackTrace()
            throw t
        }
    }
    val ticketsListViewModel = remember { GlobalContext.get().get<TicketsListViewModel>() }
    val ticketsListState by ticketsListViewModel.state.collectAsState()

    DisposableEffect(ticketViewModel) {
        ticketViewModel.onPermissionsRequired = { sessionViewModel.requireConsent() }
        onDispose { ticketViewModel.onPermissionsRequired = null }
    }

    LaunchedEffect(Unit) {
        ticketsListViewModel.loadFirstScreen()
        ticketViewModel.startV2()
    }

    when {
        !ticketsListState.initialLoadDone -> LoadingScreen()
        showBillingSuccess -> {
            Style(AppStylesGlobal)
            Style(AppStylesHeader)
            Style(AppStylesLayout)
            Style(AppStylesAccount)
            BillingSuccessScreen(onGoToTickets = {
                window.history.replaceState(null, "", "/")
                onBillingSuccessDismiss()
            })
        }
        else -> {
            val accountViewModel = remember { GlobalContext.get().get<com.appsyde.shared.app.presentation.account.AccountViewModel>() }
            MainScreen(
                ticketViewModel = ticketViewModel,
                ticketsListViewModel = ticketsListViewModel,
                accountViewModel = accountViewModel,
                logoutAction = { clearSessionForLogout(); sessionViewModel.logout() }
            )
        }
    }
}
